"use client"

import * as React from "react"
import { ChevronDownIcon } from "lucide-react"

import {
  Checkbox,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  MobilePickerSheet,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
} from "@ibada/ui"

export type InviteBranchChoice = { value: string; label: string }

/** How many chosen branch names the field spells out before "+N". */
const NAMED_IN_FIELD = 2

export type InviteBranchMultiSelectLabels = {
  placeholder: string
  search: string
  empty: string
  selectAll: string
  clear: string
  /** "Kinshasa Central, Kisangani +10". */
  summary: (names: string, count: number) => string
}

/** The field's text for the chosen branches: every name when few, else the first two and "+N". */
export function branchSelectionSummary(
  chosen: readonly InviteBranchChoice[],
  summary: InviteBranchMultiSelectLabels["summary"],
): string {
  if (chosen.length <= NAMED_IN_FIELD) return chosen.map((branch) => branch.label).join(", ")
  const names = chosen
    .slice(0, NAMED_IN_FIELD)
    .map((branch) => branch.label)
    .join(", ")
  return summary(names, chosen.length - NAMED_IN_FIELD)
}

/**
 * "Plusieurs branches": one pill field that opens a searchable list of every branch with a checkbox each, plus
 * "Tout sélectionner" / "Effacer". Workspaces can have dozens of branches, so they are never laid out in the
 * dialog itself. Phones get the app's picker sheet with the same actions.
 */
export function InviteBranchMultiSelect({
  options,
  value,
  onChange,
  labels,
  isMobile,
  title,
  disabled = false,
  invalid = false,
}: {
  options: readonly InviteBranchChoice[]
  /** The ticked branch ids, in the order they were ticked. */
  value: readonly string[]
  onChange: (next: string[]) => void
  labels: InviteBranchMultiSelectLabels
  isMobile: boolean
  /** The phone sheet's title. */
  title: string
  disabled?: boolean
  invalid?: boolean
}) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")
  const sorted = React.useMemo(
    () => [...options].sort((left, right) => left.label.localeCompare(right.label)),
    [options],
  )
  const labelById = React.useMemo(() => new Map(options.map((option) => [option.value, option])), [options])
  const selected = React.useMemo(() => new Set(value), [value])
  const chosen = value.map((id) => labelById.get(id)).filter((branch): branch is InviteBranchChoice => Boolean(branch))
  const text = branchSelectionSummary(chosen, labels.summary)

  const query = search.trim().toLowerCase()
  const matches = query ? sorted.filter((option) => option.label.toLowerCase().includes(query)) : sorted

  const toggle = (branchId: string) => {
    onChange(selected.has(branchId) ? value.filter((id) => id !== branchId) : [...value, branchId])
  }
  // "Tout sélectionner" picks what the search shows (every branch when it is empty), after what is already ticked.
  const selectAll = () => onChange([...value, ...matches.map((option) => option.value).filter((id) => !selected.has(id))])
  const clear = () => onChange([])

  const field = (
    <button
      type='button'
      disabled={disabled}
      data-invalid={invalid || undefined}
      aria-haspopup='listbox'
      onClick={isMobile ? () => setOpen(true) : undefined}
      data-testid='invite-branch-multi-select'
      className={cn(
        "border-border bg-background flex h-control w-full items-center gap-2 rounded-full border px-4 text-left text-sm transition-colors focus-visible:border-primary focus-visible:ring-primary/20 focus-visible:outline-none focus-visible:ring-3 disabled:opacity-60",
        invalid && "border-destructive",
      )}
    >
      <span className={cn("min-w-0 flex-1 truncate", chosen.length === 0 && "text-muted-foreground")}>
        {chosen.length > 0 ? text : labels.placeholder}
      </span>
      <ChevronDownIcon aria-hidden className='text-muted-foreground size-4 shrink-0' />
    </button>
  )

  if (isMobile) {
    return (
      <>
        {field}
        <MobilePickerSheet
          open={open}
          onOpenChange={setOpen}
          title={title}
          options={sorted.map((option) => ({ value: option.value, label: option.label }))}
          values={value}
          onSelect={toggle}
          searchable
          searchPlaceholder={labels.search}
          emptyLabel={labels.empty}
          actions={[
            { label: labels.selectAll, onSelect: () => onChange(sorted.map((option) => option.value)) },
            { label: labels.clear, onSelect: clear },
          ]}
        />
      </>
    )
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) setSearch("")
      }}
    >
      <PopoverTrigger asChild>{field}</PopoverTrigger>
      <PopoverContent align='start' className='w-[var(--radix-popover-trigger-width)] rounded-2xl p-0'>
        <Command shouldFilter={false}>
          <CommandInput placeholder={labels.search} value={search} onValueChange={setSearch} />
          <div className='flex items-center justify-between gap-2 px-3 pt-2 pb-1'>
            <button
              type='button'
              onClick={selectAll}
              className='text-primary rounded-full text-[13px] font-bold hover:underline focus-visible:underline focus-visible:outline-none'
            >
              {labels.selectAll}
            </button>
            <button
              type='button'
              onClick={clear}
              disabled={value.length === 0}
              className='text-muted-foreground hover:text-foreground rounded-full text-[13px] font-bold disabled:opacity-50'
            >
              {labels.clear}
            </button>
          </div>
          <CommandList>
            <CommandEmpty>{labels.empty}</CommandEmpty>
            <CommandGroup>
              {matches.map((option) => {
                const checked = selected.has(option.value)
                return (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={() => toggle(option.value)}
                    data-testid='invite-branch-option'
                    className='gap-2.5'
                  >
                    <Checkbox checked={checked} tabIndex={-1} aria-hidden className='pointer-events-none' />
                    <span className='min-w-0 flex-1 truncate'>{option.label}</span>
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
